<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import IconNetworkSignal from './IconNetworkSignal.vue';
import { IconWifiOff, Clear } from '@xpos/icons-svg';
import { NetworkQuality } from '@/types/network.type';

const props = defineProps<{
  visible: boolean;
  onLine: boolean;
  networkSpeed: number;
  networkLatency: number;
  networkLevel: NetworkQuality;
  networkStatusKey: string;
  networkPopperClass: string;
  wide?: boolean;
}>();
const emit = defineEmits<{ 'update:visible': [boolean]; dismiss: [] }>();

const { t } = useI18n();
const toggle = () => props.visible ? emit('dismiss') : emit('update:visible', true); // closing via icon also applies cooldown
const dismiss = () => emit('dismiss');
</script>
<template>
  <el-popover :visible="visible" placement="right" :width="180" effect="light" :popper-class="networkPopperClass">
    <template #reference>
      <button v-if="!wide" class="w-[50px] h-[50px] bg-white rounded-[10px] primary-color flex justify-center items-center mb-2.5 text-xl" @click.stop="toggle">
        <IconNetworkSignal v-if="onLine" :mbps="networkSpeed" :latency="networkLatency" />
        <IconWifiOff v-else color="#E53030" />
      </button>
      <button v-else class="wide-button" @click.stop="toggle">
        <el-icon class="button-icon">
          <IconNetworkSignal v-if="onLine" :mbps="networkSpeed" :latency="networkLatency" />
          <IconWifiOff v-else />
        </el-icon>
        <span class="button-text">{{ t(onLine ? 'buttons.online' : 'buttons.offline') }}</span>
      </button>
    </template>
    <div class="text-sm">
      <div class="flex justify-between items-start gap-2">
        <div class="network-title font-bold">{{ t(`network.${networkStatusKey}Title`) }}</div>
        <button class="w-[18px] h-[18px] flex items-center justify-center shrink-0 bg-[#3A3A3A] rounded-full text-white hover:bg-opacity-90 transition-opacity overflow-hidden" @click.stop="dismiss">
          <el-icon :size="16"><Clear style="stroke: currentColor; stroke-width: 1px;" /></el-icon>
        </button>
      </div>
      <div class="network-subtitle font-medium mb-2" v-if="!onLine || networkLevel !== NetworkQuality.Healthy">{{ t(`network.${networkStatusKey}Subtitle`) }}</div>
      <template v-if="onLine">
        <div class="text-[#3A3A3A]">{{ t('network.latency') }}: {{ networkLatency }}ms</div>
        <div class="text-[#3A3A3A]">{{ t('network.bandwidth') }}: {{ Math.round(networkSpeed) }} mbps</div>
      </template>
    </div>
  </el-popover>
</template>
<style lang="sass" scoped>
.wide-button
  display: flex
  align-items: center
  gap: 12px
  padding: 12px 16px
  background: white
  border: none
  border-radius: 12px
  margin: 4px 16px
  cursor: pointer
  transition: all 0.2s ease
  text-align: left
  min-height: 48px
  &:hover
    background: #f5f5f5
.button-icon
  width: 20px
  height: 20px
  color: #52599D
.button-text
  font-size: 14px
  color: #52599D
  font-weight: 500
</style>
